import { IUser } from '@/types';
import { MANAGER_LABELS } from '@/constants/labels';
import { TextContent } from '@/components/Text';
import { useCurrentUserFeature } from '@/features/users';
import { UserInfo } from '@/components/Users';

/**
 * Title and description for the ManagerDashboard are included in the DashboardContent
 * component to ensure they are visible even when user data is loading. The UserInfo component
 * will display skeletons for the user's name and role until the data is available.
 */
const { TEAM_DASHBOARD_TITLE, TEAM_DASHBOARD_DESCRIPTION } = MANAGER_LABELS;

/** Props for the ManagerDashboardHeader component */
interface ManagerDashboardHeaderProps {
  /** The current user object */
  currentUser: IUser | null;
  /** Loading state for the dashboard header */
  isLoading: boolean;
}

/**
 * ManagerDashboardHeader Component
 *
 * This component renders the header of the ManagerDashboard, displaying the dashboard title,
 * description, and user information. It uses the currentUser prop to display the user's name
 * and role, and it handles the loading state to show skeletons when data is being fetched.
 */
export const ManagerDashboardHeader: React.FC<ManagerDashboardHeaderProps> = ({
  currentUser,
  isLoading,
}) => {
  const { currentUserFullName, currentUserRole, currentUserImageUrl } = useCurrentUserFeature({
    currentUser,
  });

  // ===============================
  // Render
  // ===============================
  return (
    <div className="px-0 pb-6">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-12 lg:gap-6">
        <div className="col-span-12 space-y-2 md:col-span-7 lg:col-span-9">
          <TextContent
            isLoading={isLoading}
            label={TEAM_DASHBOARD_TITLE}
            level={1}
            type="headline"
            variant="pageTitle"
          />
          <TextContent
            className="max-w-xl"
            color="neutral-gray-700"
            isLoading={isLoading}
            label={TEAM_DASHBOARD_DESCRIPTION}
            type="paragraph"
            variant="bodyMedium"
          />
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="ml-auto flex translate-y-1 items-center space-x-4">
            <UserInfo
              contentAlignment="right"
              imageSize={56}
              imageUrl={currentUserImageUrl}
              isLoading={isLoading}
              name={currentUserFullName}
              textAlignment="right"
              textSide="left"
              title={currentUserRole}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ManagerDashboardHeader.displayName = 'ManagerDashboardHeader';

export default ManagerDashboardHeader;
